import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { LifeBuoy, ShieldAlert } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { CopyableId } from '@/components/app/CopyableId';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import { StatCard } from '@/components/app/StatCard';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { humanizeError } from '@/lib/apiClient';
import { shortId } from '@/lib/utils';
import { useSupportList, useSupportStats } from '../hooks';
import type {
  SupportPriority,
  SupportRequestStatus,
  SupportRequestType,
  SupportTicket,
} from '../types';

const STATUS_OPTIONS: { value: 'all' | SupportRequestStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_OPTIONS: { value: 'all' | SupportRequestType; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'bug_report', label: 'Bug' },
  { value: 'feature_request', label: 'Feature' },
  { value: 'account_issue', label: 'Account' },
  { value: 'billing_payments', label: 'Billing' },
  { value: 'data_privacy', label: 'Privacy' },
  { value: 'safety_report', label: 'Safety' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS: { value: 'all' | SupportPriority; label: string }[] = [
  { value: 'all', label: 'Any priority' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

function statusVariant(s: SupportRequestStatus): 'success' | 'warning' | 'outline' | 'default' {
  if (s === 'resolved' || s === 'closed') return 'success';
  if (s === 'open' || s === 'triaged') return 'warning';
  if (s === 'in_progress') return 'default';
  return 'outline';
}

function priorityVariant(p?: SupportPriority): 'destructive' | 'warning' | 'outline' {
  if (p === 'high') return 'destructive';
  if (p === 'normal') return 'warning';
  return 'outline';
}

export function SupportListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<'all' | SupportRequestStatus>('all');
  const [type, setType] = React.useState<'all' | SupportRequestType>('all');
  const [priority, setPriority] = React.useState<'all' | SupportPriority>('all');
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);

  const params = {
    limit: 50,
    cursor,
    status: status === 'all' ? undefined : status,
    type: type === 'all' ? undefined : type,
    priority: priority === 'all' ? undefined : priority,
  };
  const list = useSupportList(params);
  const stats = useSupportStats();

  const columns = React.useMemo<ColumnDef<SupportTicket>[]>(() => {
    return [
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>,
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1 text-xs">
            {row.original.type === 'safety_report' && (
              <ShieldAlert className="h-3 w-3 text-destructive" />
            )}
            {row.original.type.replace('_', ' ')}
          </span>
        ),
      },
      {
        id: 'user',
        header: 'User',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-xs text-muted-foreground">{row.original.userEmail ?? '—'}</div>
            <CopyableId
              value={row.original.userId}
              display={shortId(row.original.userId, 8, 6)}
              label="User ID"
              className="-ml-1.5"
            />
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span className="line-clamp-2 max-w-lg text-sm text-muted-foreground">
            {row.original.description}
          </span>
        ),
      },
      {
        id: 'priority',
        header: 'Priority',
        cell: ({ row }) => (
          <Badge variant={priorityVariant(row.original.priority)}>
            {row.original.priority ?? 'normal'}
          </Badge>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.updatedAt.slice(0, 10)}
          </span>
        ),
      },
    ];
  }, []);

  const counts = stats.data?.counts;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Support inbox"
        description="Tickets from the in-app help form. Status changes are audited."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as 'all' | SupportRequestStatus);
                setCursor(undefined);
                setCursorStack([]);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={type}
              onValueChange={(v) => {
                setType(v as 'all' | SupportRequestType);
                setCursor(undefined);
                setCursorStack([]);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={priority}
              onValueChange={(v) => {
                setPriority(v as 'all' | SupportPriority);
                setCursor(undefined);
                setCursorStack([]);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {counts && (
        <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard label="Open" value={counts.open ?? 0} icon={LifeBuoy} />
          <StatCard label="Triaged" value={counts.triaged ?? 0} />
          <StatCard label="In progress" value={counts.in_progress ?? 0} />
          <StatCard label="Resolved" value={counts.resolved ?? 0} />
          <StatCard label="Closed" value={counts.closed ?? 0} />
        </section>
      )}

      {list.isError ? (
        <ErrorState
          title="Tickets failed to load"
          description={humanizeError(list.error)}
          actionLabel="Retry"
          onAction={() => list.refetch()}
        />
      ) : (
        <DataTable<SupportTicket>
          columns={columns}
          data={list.data?.tickets ?? []}
          loading={list.isLoading || list.isFetching}
          emptyLabel="No tickets match these filters"
          rowKey={(r) => r.supportRequestId}
          onRowClick={(r) => navigate(`/support/${encodeURIComponent(r.supportRequestId)}`)}
          pagination={{
            cursor: cursor ?? null,
            nextCursor: list.data?.nextCursor ?? null,
            prevCursor: cursorStack.length > 0 ? (cursorStack[cursorStack.length - 1] ?? null) : null,
            hasMore: !!list.data?.nextCursor,
            onNext: list.data?.nextCursor
              ? () => {
                  setCursorStack((stack) => [...stack, cursor ?? '']);
                  setCursor(list.data?.nextCursor ?? undefined);
                }
              : undefined,
            onPrev:
              cursorStack.length > 0
                ? () => {
                    const prev = cursorStack[cursorStack.length - 1] || undefined;
                    setCursor(prev);
                    setCursorStack((stack) => stack.slice(0, -1));
                  }
                : undefined,
          }}
        />
      )}
    </div>
  );
}
